/**
 * <sticky-buy-bar>
 *
 * A theme-native sticky Add-to-Cart bar for the product page, built to replace
 * the Corner Cart app's sticky ATC.
 *
 * Design rule: THE BAR OWNS NO STATE.
 * The PDP <variant-picker> is the single source of truth. The bar is a view
 * plus a proxy, so the two can never drift apart:
 *
 *   bar -> PDP   clicks the real <label class="opt-label">, which is byte-for-byte
 *                what a shopper's tap does. That checks the radio, bubbles a
 *                `change` to <variant-picker>, AND fires the jQuery .opt-label
 *                handler in snippets/variant-picker.liquid that mirrors price
 *                into the .r-price / .s-price pack tiles. Dispatching `change`
 *                on the radio alone would skip that handler and leave the pack
 *                tiles showing a stale price.
 *
 *   PDP -> bar   listens for `on:variant:change` (dispatched by variant-picker.js)
 *                and re-reads price / availability straight out of the PDP DOM.
 *
 *   add to cart  clicks the real [name="add"] inside .js-product-form-main, so
 *                submission runs through product-form.js exactly as it does
 *                today - including whatever opens the GoKwik cart afterwards.
 *                No cart logic is duplicated here.
 */
if (!customElements.get('sticky-buy-bar')) {
  class StickyBuyBar extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.section = this.closest('.js-product') || document.querySelector('.js-product');
      if (!this.section) return;

      this.picker = this.section.querySelector('variant-picker');
      this.form = this.section.querySelector('.js-product-form-main');
      this.realAtc = this.form ? this.form.querySelector('[name="add"]') : null;
      this.anchor = this.section.querySelector('.product-info__add-to-cart');

      // Without a real button to proxy, the bar has nothing to offer.
      if (!this.realAtc || !this.anchor) return;

      this.optionsEl = this.querySelector('.js-sbb-options');
      this.priceEl = this.querySelector('.js-sbb-price');
      this.atcBtn = this.querySelector('.js-sbb-atc');
      this.imgEl = this.querySelector('.js-sbb-img');

      // Second CTA (mobile). If GoKwik's button is not on the page there is
      // nothing to proxy to, so drop ours rather than render a dead control.
      this.buyBtn = this.querySelector('.js-sbb-buynow');
      this.realBuy = document.getElementById('gokwik-buy-now');
      if (this.buyBtn && !this.realBuy) {
        this.buyBtn.remove();
        this.buyBtn = null;
      }

      this.buildOptions();
      this.sync();
      this.bindEvents();

      document.body.classList.add('pdp-has-sticky-buy-bar');
    }

    bindEvents() {
      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', this.handleAtcClick.bind(this));
      }

      if (this.buyBtn) {
        this.buyBtn.addEventListener('click', this.handleBuyClick.bind(this));
      }

      // PDP -> bar
      this.section.addEventListener('on:variant:change', (evt) => {
        this.sync();
        if (evt.detail) this.updateImage(evt.detail.variant);
      });

      // Mirror the real button's loading / disabled state onto ours.
      if ('MutationObserver' in window) {
        this.btnObserver = new MutationObserver(() => this.syncButton());
        this.btnObserver.observe(this.realAtc, {
          attributes: true,
          attributeFilter: ['class', 'disabled', 'aria-disabled']
        });

        // GoKwik flips #gokwik-buy-now from disabled to enabled when its
        // script finishes loading, so watch for that rather than sampling once.
        if (this.realBuy) {
          this.buyObserver = new MutationObserver(() => this.syncBuyButton());
          this.buyObserver.observe(this.realBuy, {
            attributes: true,
            attributeFilter: ['class', 'disabled']
          });
        }
      }

      // Show the bar only once the real Add to cart has scrolled off the top.
      // Before that point the shopper can already see the real button, so a
      // second one would just cover the page for no gain.
      if ('IntersectionObserver' in window) {
        this.visObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.classList.toggle('is-visible', scrolledPast);
          });
        }, { threshold: 0 });
        this.visObserver.observe(this.anchor);
      }
    }

    disconnectedCallback() {
      if (this.btnObserver) this.btnObserver.disconnect();
      if (this.buyObserver) this.buyObserver.disconnect();
      if (this.visObserver) this.visObserver.disconnect();
      document.body.classList.remove('pdp-has-sticky-buy-bar');
    }

    /**
     * Builds one <select> per option selector, mirroring the values the theme
     * actually rendered. It reads the DOM rather than the product JSON on
     * purpose: variant-picker.liquid only renders values that are in stock
     * (e.g. 4 of 14 flavours on the ABE pre-workout), so re-deriving the list
     * from product.options would offer flavours that cannot be bought.
     */
    buildOptions() {
      this.optionGroups = [];
      if (!this.picker || !this.optionsEl) return;

      const selectors = this.picker.querySelectorAll('.option-selector[data-selector-type="listed"]');

      selectors.forEach((selector) => {
        const radios = Array.from(selector.querySelectorAll('input.js-option'));
        if (radios.length < 2) return; // single value: nothing to choose

        const legend = selector.querySelector('legend');
        const name = legend ? legend.textContent.split(':')[0].trim() : 'Options';

        const select = document.createElement('select');
        select.className = 'sticky-buy-bar__select';
        select.setAttribute('aria-label', name);

        radios.forEach((radio) => {
          const option = document.createElement('option');
          option.value = radio.value;
          option.textContent = radio.value;
          select.appendChild(option);
        });

        select.addEventListener('change', () => this.applySelection(selector, select.value));

        const wrap = document.createElement('div');
        wrap.className = 'sticky-buy-bar__option';
        wrap.appendChild(select);
        this.optionsEl.appendChild(wrap);

        this.optionGroups.push({ selector, select, radios });
      });
    }

    /**
     * Pushes a bar selection back into the PDP picker.
     * @param {Element} selector - The .option-selector this select mirrors.
     * @param {string} value - The chosen option value.
     */
    applySelection(selector, value) {
      const radio = Array.from(selector.querySelectorAll('input.js-option'))
        .find((r) => r.value === value);
      if (!radio || radio.checked) return;

      const label = selector.querySelector(`label[for="${radio.id}"]`);

      if (label) {
        // Same path as a real tap - see the note at the top of this file.
        label.click();
      } else {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /**
     * Re-reads price, selection and button state out of the PDP.
     */
    sync() {
      this.optionGroups.forEach((group) => {
        const checked = group.selector.querySelector('input.js-option:checked');
        if (checked) group.select.value = checked.value;

        Array.from(group.select.options).forEach((option) => {
          const radio = group.radios.find((r) => r.value === option.value);
          option.disabled = !!(radio && radio.classList.contains('is-nonexistent'));
        });
      });

      // Copy the PDP's own rendered price so currency formatting, the rupee
      // font and the sale styling are identical by construction.
      const pdpPrice = this.section.querySelector('.product-info__price > .price');
      if (pdpPrice && this.priceEl) this.priceEl.innerHTML = pdpPrice.outerHTML;

      this.syncButton();
      this.syncBuyButton();
    }

    syncBuyButton() {
      if (!this.buyBtn || !this.realBuy) return;
      this.buyBtn.disabled = this.realBuy.disabled
        || this.realBuy.classList.contains('disabled');
    }

    syncButton() {
      if (!this.atcBtn || !this.realAtc) return;

      const label = this.realAtc.textContent.trim();
      if (label && this.atcBtn.textContent.trim() !== label) this.atcBtn.textContent = label;

      this.atcBtn.disabled = this.realAtc.disabled;
      this.atcBtn.classList.toggle('is-loading', this.realAtc.classList.contains('is-loading'));
      this.atcBtn.classList.toggle('is-success', this.realAtc.classList.contains('is-success'));
    }

    updateImage(variant) {
      if (!this.imgEl || !variant || !variant.featured_media) return;
      const preview = variant.featured_media.preview_image;
      const src = preview && preview.src;
      if (!src) return;

      this.imgEl.src = `${src}${src.indexOf('?') > -1 ? '&' : '?'}width=120`;
      this.imgEl.removeAttribute('srcset');
    }

    handleAtcClick(evt) {
      evt.preventDefault();
      if (!this.realAtc) return;
      if (this.realAtc.disabled) return;
      if (this.realAtc.getAttribute('aria-disabled') === 'true') return;

      // Hand straight over to the real button: product-form.js takes it from
      // here, exactly as if the shopper had used the button up the page.
      this.realAtc.click();
    }

    handleBuyClick(evt) {
      evt.preventDefault();
      if (!this.realBuy) return;
      if (this.realBuy.disabled || this.realBuy.classList.contains('disabled')) return;

      // onclick="onBuyNowClick(this)" on the real button receives the real
      // button, which is what GoKwik expects.
      this.realBuy.click();
    }
  }

  customElements.define('sticky-buy-bar', StickyBuyBar);
}
